import { useCallback, useEffect, useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import mapKeys from 'lodash/mapKeys';
import cloneDeep from 'lodash/cloneDeep';
import { getRequest, request } from 'cn-request';
const SchemaMap = {
    display: 'x-display',
    component: 'x-component',
    uipattern: 'x-pattern',
    dataSource: 'enum',
    reactions: 'x-reactions',
    'component-props': 'x-component-props',
};
const CnCard = 'CnCard';
const CnCardSubCard = 'CnCardSubCard';
const CnFormGrid = 'CnFormGrid';
const ExtraSchema = { 'x-decorator': 'CnFormItem' };
const formattedSchemaKeys = (obj) => mapKeys(obj, (value, key) => SchemaMap?.[key] ?? key);
const generateFormItemSchema = (item, cols) => {
    const { name, title, 'component-props': componentProps = {}, ...restProps } = item;
    const { cols: subCardCols } = componentProps;
    const formGridProps = {};
    if (subCardCols) {
        formGridProps.cols = subCardCols;
    }
    else {
        formGridProps.cols = cols;
    }
    if (item.component === CnCard) {
        return {
            type: 'void',
            'x-component': CnCard,
            'x-component-props': {
                title,
                foldable: true,
                style: { marginBottom: '16px' },
                ...componentProps,
            },
            properties: {
                [`${name}_${CnFormGrid}`]: {
                    type: 'void',
                    'x-component': CnFormGrid,
                    'x-component-props': formGridProps,
                    properties: {},
                },
            },
            ...restProps,
        };
    }
    else if (item.component === CnCardSubCard) {
        return {
            type: 'void',
            'x-component': CnCardSubCard,
            'x-component-props': {
                subTitle: title,
                ...componentProps,
            },
            properties: {
                [`${name}_${CnFormGrid}`]: {
                    type: 'void',
                    'x-component': CnFormGrid,
                    'x-component-props': formGridProps,
                    properties: {},
                },
            },
            ...restProps,
        };
    }
    else {
        return {
            ...ExtraSchema,
            ...formattedSchemaKeys(item),
        };
    }
};
const list2FormilySchema = (list, cols) => {
    let currentParent = 'root';
    const tree = {
        root: {
            type: 'object',
            properties: {
                [`root_${CnFormGrid}`]: {
                    type: 'void',
                    'x-component': CnFormGrid,
                    'x-component-props': {
                        cols,
                    },
                    properties: {},
                },
            },
            _component: 'root',
            _name: 'root',
        },
    };
    if (Array.isArray(list) && list.length > 0) {
        const _list = cloneDeep(list);
        const ifContainer = (component) => component === 'CnCard' || component === 'CnCardSubCard';
        _list.forEach((item = {}, index) => {
            const { component } = item;
            const isContainer = ifContainer(component);
            const parent = tree[currentParent];
            if (isContainer) {
                item.name = `container_${component}_${index}`;
            }
            const { name } = item;
            if (isContainer) {
                if (component === CnCard) {
                    tree[name] = generateFormItemSchema(item, cols);
                    tree[name]._parent = 'root';
                }
                else if (component === CnCardSubCard) {
                    if (parent?._component === CnCard || parent?._component === 'root') {
                        tree[name] = generateFormItemSchema(item, cols);
                        if (tree[name]) {
                            tree[name]._parent = currentParent;
                        }
                    }
                    else if (parent?._component === CnCardSubCard) {
                        const temp = parent?._parent || 'root';
                        tree[name] = generateFormItemSchema(item, cols);
                        if (tree[name]) {
                            tree[name]._parent = tree[temp]._name || 'root';
                        }
                    }
                }
                // 先判断是否是「容器」，是的话，parant指针指向最近的容器。那么下一个input的_parent就会指向最近的容器
                currentParent = name;
            }
            else {
                tree[name] = generateFormItemSchema(item, cols);
                if (tree[name]) {
                    tree[name]._parent = currentParent;
                }
            }
            if (tree[name]) {
                tree[name]._name = name;
                tree[name]._component = component;
            }
            if (tree[name]?._parent) {
                const parentSchema = tree[tree[name]._parent];
                if (parentSchema._component === CnCardSubCard) {
                    const { _name } = parentSchema;
                    const formGrid = parentSchema.properties[`${_name}_${CnFormGrid}`];
                    if (formGrid?.properties) {
                        formGrid.properties[name] = tree[name];
                    }
                }
                else if (parentSchema._component === 'root' && !isContainer) {
                    const formGrid = parentSchema.properties[`root_${CnFormGrid}`];
                    if (formGrid?.properties) {
                        formGrid.properties[name] = tree[name];
                    }
                }
                else if (parentSchema._component === CnCard) {
                    if (component === CnCardSubCard) {
                        parentSchema.properties[name] = tree[name];
                    }
                    else {
                        const { _name } = parentSchema;
                        const formGrid = parentSchema.properties[`${_name}_${CnFormGrid}`];
                        if (formGrid?.properties) {
                            formGrid.properties[name] = tree[name];
                        }
                    }
                }
                else {
                    parentSchema.properties[name] = tree[name];
                }
            }
        });
    }
    return tree.root;
};
export const generateSchema = (schema, cols, onlyProperties = false) => {
    if (isEmpty(schema))
        return {};
    if (!isArray(schema))
        return schema;
    const finalSchema = list2FormilySchema(schema, cols);
    if (onlyProperties) {
        return finalSchema.properties[`root_${CnFormGrid}`].properties;
    }
    return finalSchema;
};
const packRequest = (options) => {
    if (!getRequest()) {
        console.error('组件使用默认实例中...请先初始化项目cn-request,切勿发布到生产环境');
    }
    return request(options).then((result) => {
        if ('request' in result && 'config' in result) {
            return result.data;
        }
        return result;
    });
};
export const handleRequestService = (requestConfig) => {
    const { url, service, params: paramsProps, data: dataProps, searchFormat = (data) => data, method = 'GET', } = requestConfig || {};
    if (service) {
        return service;
    }
    if (url) {
        return (searchConfig) => {
            const { formValues } = searchConfig || {};
            const params = method?.toLocaleUpperCase() === 'GET'
                ? searchFormat({ ...formValues, ...paramsProps })
                : paramsProps;
            const data = method?.toLocaleUpperCase() === 'POST'
                ? searchFormat({ ...formValues, ...dataProps })
                : dataProps;
            return packRequest({
                ...requestConfig,
                params,
                data,
                url,
            });
        };
    }
    return () => Promise.resolve([]);
};
export function useStateCallback(initialState) {
    const [state, setState] = useState(initialState);
    const cbRef = useRef(null);
    const setStateCallback = useCallback((_state, cb) => {
        cbRef.current = cb;
        setState(_state);
    }, []);
    useEffect(() => {
        if (cbRef.current) {
            cbRef.current(state);
            cbRef.current = null;
        }
    }, [state]);
    return [state, setStateCallback];
}
export function useIsExceedWidth(width) {
    const [isExceed, setIsExceed] = useState(document.documentElement.clientWidth >= width);
    const resizeHandle = useCallback(() => {
        setIsExceed(document.documentElement.clientWidth >= width);
    }, []);
    useEffect(() => {
        window.addEventListener('resize', resizeHandle);
        return () => window.removeEventListener('resize', resizeHandle);
    }, []);
    return isExceed;
}
